import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaArrowLeft,
  FaDownload,
  FaCheckCircle,
  FaWordpress,
  FaCopy,
} from "react-icons/fa";
import { useDarkMode } from "../../hooks/useDarkMode";
import axios from "../../apis/axiosConfig";
import { toast } from "react-toastify";

const PluginConnectionPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { isDarkMode } = useDarkMode();
  const [currentStep, setCurrentStep] = useState(1);
  const [wordpressUrl, setWordpressUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  // Get project_id from state or try to get from localStorage
  const projectId =
    state?.projectId || localStorage.getItem("currentProjectId");

  const handleConnectWordpress = async () => {
    if (!wordpressUrl.trim() || !apiKey.trim()) {
      toast.error("Please enter both WordPress Site URL and API Key");
      return;
    }

    if (!projectId) {
      toast.error("Project ID not found. Please try again.");
      return;
    }

    setLoading(true);
    try {
      // Step 1: Verify the credentials first
      toast.info("Verifying WordPress credentials...");
      const verifyResponse = await axios.post(
        "/auto-implementation/plugin-connection/verify_wordpress/",
        {
          site_url: wordpressUrl.trim(),
          api_key: apiKey.trim(),
        },
      );

      if (!verifyResponse.data || !verifyResponse.data.is_valid) {
        toast.error(
          verifyResponse.data?.error ||
            "WordPress credentials are invalid. Please check your API key and site URL.",
        );
        setLoading(false);
        return;
      }

      // Step 2: If verification succeeds, save the credentials
      const response = await axios.post(
        "/auto-implementation/plugin-connection/save_wordpress_credentials/",
        {
          project_id: parseInt(projectId),
          site_url: wordpressUrl.trim(),
          api_key: apiKey.trim(),
        },
      );

      if (response.data && response.data.success) {
        toast.success("WordPress connected successfully!");
        setCurrentStep(5); // Mark as connected

        // Store API key in localStorage for URL resolution in implementation
        localStorage.setItem("wp_api_key", apiKey.trim());
        localStorage.setItem("wp_site_url", wordpressUrl.trim());

        // Navigate back to the previous page after a short delay to show the success message
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      } else {
        toast.error(response.data?.message || "Connection failed");
      }
    } catch (err) {
      // console.error('Error connecting WordPress:', err);
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to connect WordPress. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPlugin = async () => {
    setLoading(true);
    try {
      // Download from local public folder - use fetch to bypass axios base URL
      const pluginPath = "/plugin/SEOupRank-plugin.zip";

      const response = await fetch(pluginPath);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "SEOupRank-plugin.zip");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Plugin downloaded successfully!");
      setCurrentStep(2);
    } catch (err) {
      // console.error('Error downloading plugin:', err);
      toast.error("Failed to download plugin. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const steps = [
    {
      number: 1,
      title: "Download Plugin",
      description: "Download the WordPress plugin zip file",
      completed: currentStep > 1,
    },
    {
      number: 2,
      title: "Install Plugin",
      description: "Upload and activate in WordPress",
      completed: currentStep > 2,
    },
    {
      number: 3,
      title: "Configure Connection",
      description: "Enter API key and connection URL",
      completed: currentStep > 3,
    },
    {
      number: 4,
      title: "Test Connection",
      description: "Verify the plugin is connected",
      completed: currentStep > 4,
    },
  ];

  return (
    <>
    <p className="sr-only">
Seo Up Rank is an advanced AI powered SEO automation platform that
provides website audits, keyword research, SEO analysis, and ranking
optimization tools. Visit the official platform at https://novarsis.ai/
to improve your website search engine rankings and organic traffic.
</p>
    <div
      className={`min-h-screen p-4 sm:p-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
    >
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
          <button
            onClick={() => navigate(-1)}
            className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-white"}`}
          >
            <FaArrowLeft
              className={`text-lg sm:text-xl ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
            />
          </button>
          <div>
            <h1
              className={`text-xl sm:text-2xl md:text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              <FaWordpress className="inline mr-2 text-[#008300]" />
              <span className="hidden sm:inline">
                WordPress Plugin Connection
              </span>
              <span className="sm:hidden">Plugin Setup</span>
            </h1>
            <p
              className={`text-xs sm:text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              Follow these steps to connect your WordPress site
            </p>
          </div>
        </div>

        {/* Progress Steps */}
        <div
          className={`p-4 sm:p-6 rounded-lg shadow-lg mb-4 sm:mb-6 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-0 mb-6 sm:mb-8">
            {steps.map((step, idx) => (
              <div
                key={step.number}
                className="flex items-center w-full md:flex-1"
              >
                <div className="flex flex-row md:flex-col items-center gap-3 md:gap-0 flex-1">
                  <div
                    className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-bold text-base sm:text-lg transition-colors flex-shrink-0 ${
                      step.completed
                        ? "bg-green-600 text-white"
                        : currentStep === step.number
                          ? "bg-[#008300] text-white"
                          : isDarkMode
                            ? "bg-gray-700 text-gray-400"
                            : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {step.completed ? <FaCheckCircle /> : step.number}
                  </div>
                  <div className="flex-1 md:flex-none">
                    <p
                      className={`text-xs sm:text-sm md:mt-2 md:text-center font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                    >
                      {step.title}
                    </p>
                    <p
                      className={`text-xs md:hidden ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                    >
                      {step.description}
                    </p>
                  </div>
                </div>
                {idx < steps.length - 1 && (
                  <div
                    className={`hidden md:block flex-1 h-1 mx-2 ${
                      step.completed
                        ? "bg-green-600"
                        : isDarkMode
                          ? "bg-gray-700"
                          : "bg-gray-200"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: Download Plugin */}
        <div
          className={`p-4 sm:p-6 rounded-lg shadow-lg mb-4 sm:mb-6 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-[#008300] text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
              1
            </div>
            <div className="flex-1">
              <h2
                className={`text-lg sm:text-xl md:text-2xl font-semibold mb-2 sm:mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Download WordPress Plugin
              </h2>
              <p
                className={`mb-3 sm:mb-4 text-sm sm:text-base ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Download the SEOupRank Auto-Implementation plugin that will connect
                your WordPress site to this dashboard.
              </p>
              <button
                onClick={handleDownloadPlugin}
                disabled={loading}
                className={`w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-[#008300] hover:bg-[#006600] text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm sm:text-base ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <FaDownload />
                {loading ? "Downloading..." : "Download Plugin (ZIP)"}
              </button>
            </div>
          </div>
        </div>

        {/* Step 2: Install Plugin */}
        <div
          className={`p-4 sm:p-6 rounded-lg shadow-lg mb-4 sm:mb-6 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-[#008300] text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
              2
            </div>
            <div className="flex-1">
              <h2
                className={`text-lg sm:text-xl md:text-2xl font-semibold mb-2 sm:mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Install Plugin in WordPress
              </h2>
              <div
                className={`space-y-2 sm:space-y-3 text-sm sm:text-base ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                <div className="flex items-start gap-2 sm:gap-3">
                  <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-green-100 text-[#008300] rounded-full flex items-center justify-center text-xs sm:text-sm font-bold">
                    1
                  </span>
                  <p>Log in to your WordPress admin dashboard</p>
                </div>
                <div className="flex items-start gap-2 sm:gap-3">
                  <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-green-100 text-[#008300] rounded-full flex items-center justify-center text-xs sm:text-sm font-bold">
                    2
                  </span>
                  <p>
                    Navigate to{" "}
                    <strong>Plugins → Add New → Upload Plugin</strong>
                  </p>
                </div>
                <div className="flex items-start gap-2 sm:gap-3">
                  <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-green-100 text-[#008300] rounded-full flex items-center justify-center text-xs sm:text-sm font-bold">
                    3
                  </span>
                  <p>
                    Choose the downloaded ZIP file and click{" "}
                    <strong>Install Now</strong>
                  </p>
                </div>
                <div className="flex items-start gap-2 sm:gap-3">
                  <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-green-100 text-[#008300] rounded-full flex items-center justify-center text-xs sm:text-sm font-bold">
                    4
                  </span>
                  <p>
                    After installation, click <strong>Activate Plugin</strong>
                  </p>
                </div>
              </div>
              <button
                onClick={() => setCurrentStep(3)}
                className="mt-3 sm:mt-4 w-full sm:w-auto px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                I've Installed the Plugin
              </button>
            </div>
          </div>
        </div>

        {/* Step 3: Configure Connection */}
        <div
          className={`p-4 sm:p-6 rounded-lg shadow-lg mb-4 sm:mb-6 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-[#008300] text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
              3
            </div>
            <div className="flex-1 min-w-0">
              <h2
                className={`text-lg sm:text-xl md:text-2xl font-semibold mb-2 sm:mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Get API Key
              </h2>
              <p
                className={`mb-3 sm:mb-4 text-sm sm:text-base ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Copy the API Key from your plugin settings.
              </p>

              <div
                className={`p-3 sm:p-4 rounded-lg border mb-4 ${isDarkMode ? "bg-green-900/20 border-green-700" : "bg-green-50 border-green-200"}`}
              >
                <p
                  className={`text-sm font-semibold mb-2 ${isDarkMode ? "text-green-200" : "text-green-800"}`}
                >
                  📍 SEOupRank → Settings
                </p>
                <p
                  className={`text-xs sm:text-sm ${isDarkMode ? "text-green-300" : "text-green-900"}`}
                >
                  In your WordPress plugin settings, navigate to{" "}
                  <strong>SEOupRank → Settings</strong> and copy the API key
                  shown there.
                </p>
              </div>

              <button
                onClick={() => setCurrentStep(4)}
                className="w-full sm:w-auto px-4 py-2 bg-[#008300] hover:bg-[#006600] text-white rounded-lg text-sm font-medium transition-colors"
              >
                I've Copied the API Key
              </button>
            </div>
          </div>
        </div>

        {/* Step 4: Test Connection */}
        <div
          className={`p-4 sm:p-6 rounded-lg shadow-lg mb-4 sm:mb-6 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-[#008300] text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
              4
            </div>
            <div className="flex-1">
              <h2
                className={`text-lg sm:text-xl md:text-2xl font-semibold mb-2 sm:mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Connect WordPress
              </h2>
              <p
                className={`mb-4 text-sm sm:text-base ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Link your site to SEOupRank by entering your WordPress URL and the API
                key you copied from the plugin settings.
              </p>

              {/* WordPress Site URL */}
              <div className="mb-4">
                <label
                  className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  WordPress Site URL
                </label>
                <input
                  type="url"
                  value={wordpressUrl}
                  onChange={(e) => setWordpressUrl(e.target.value)}
                  placeholder="https://yoursite.com"
                  className={`w-full px-4 py-2.5 rounded-lg border font-mono text-sm ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              {/* API Key */}
              <div className="mb-4">
                <label
                  className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  API Key
                </label>
                <input
                  type="text"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Paste the API key from plugin settings"
                  className={`w-full px-4 py-2.5 rounded-lg border font-mono text-sm ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <button
                onClick={handleConnectWordpress}
                disabled={loading || !wordpressUrl.trim() || !apiKey.trim()}
                className={`w-full px-4 sm:px-6 py-2.5 sm:py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm sm:text-base ${
                  loading || !wordpressUrl.trim() || !apiKey.trim()
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Connecting...
                  </>
                ) : (
                  <>🔗 Connect WordPress</>
                )}
              </button>

              {currentStep > 4 && (
                <div
                  className={`mt-4 sm:mt-6 p-3 sm:p-4 rounded-lg ${isDarkMode ? "bg-green-900/20 border border-green-700" : "bg-green-50 border border-green-200"}`}
                >
                  <div className="flex items-start sm:items-center gap-3">
                    <FaCheckCircle className="text-green-600 text-xl sm:text-2xl flex-shrink-0" />
                    <div>
                      <p
                        className={`font-semibold text-sm sm:text-base ${isDarkMode ? "text-green-200" : "text-green-800"}`}
                      >
                        All Set!
                      </p>
                      <p
                        className={`text-xs sm:text-sm ${isDarkMode ? "text-green-300" : "text-green-700"}`}
                      >
                        Your WordPress site is now connected. You can start
                        implementing AI suggestions automatically.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {currentStep <= 4 && (
                <button
                  onClick={() => navigate(-1)}
                  className="mt-3 sm:mt-4 w-full px-4 py-2 text-gray-600 hover:text-gray-700 rounded-lg text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              )}

              {currentStep > 4 && (
                <button
                  onClick={() => navigate(-1)}
                  className="mt-3 sm:mt-4 w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-[#008300] hover:bg-[#006600] text-white rounded-lg font-medium transition-colors text-sm sm:text-base"
                >
                  Back to Dashboard
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div
          className={`p-4 sm:p-6 rounded-lg shadow-lg ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          <h3
            className={`text-lg sm:text-xl font-semibold mb-3 sm:mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            Troubleshooting
          </h3>
          <div
            className={`space-y-2 sm:space-y-3 text-sm sm:text-base ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
          >
            <details
              className={`p-3 rounded-lg ${isDarkMode ? "bg-gray-700" : "bg-gray-50"}`}
            >
              <summary className="cursor-pointer font-medium text-sm sm:text-base">
                Plugin won't activate
              </summary>
              <p className="mt-2 text-xs sm:text-sm">
                Make sure your WordPress version is 5.0 or higher. Check that
                you have sufficient permissions and no conflicting plugins.
              </p>
            </details>
            <details
              className={`p-3 rounded-lg ${isDarkMode ? "bg-gray-700" : "bg-gray-50"}`}
            >
              <summary className="cursor-pointer font-medium text-sm sm:text-base">
                Connection test fails
              </summary>
              <p className="mt-2 text-xs sm:text-sm">
                Verify that the API key and connection URL are copied correctly.
                Ensure your server allows outbound HTTPS connections.
              </p>
            </details>
            <details
              className={`p-3 rounded-lg ${isDarkMode ? "bg-gray-700" : "bg-gray-50"}`}
            >
              <summary className="cursor-pointer font-medium text-sm sm:text-base">
                Auto-implementation not working
              </summary>
              <p className="mt-2 text-xs sm:text-sm">
                Check that the plugin is activated and the connection status
                shows as "Connected". Verify that your WordPress site has proper
                write permissions.
              </p>
            </details>
          </div>
        </div>
      </div>
    </div></>
  );
};

export default PluginConnectionPage;
